import { Capacitor } from '@capacitor/core';
import { fetchApi } from '../lib/apiClient';
import { pushyManager } from './pushyManager';
import { logger } from '../lib/logger';
import { frontendLogger } from '../atoms/v3/debugAtoms';

// Pushy 全局对象声明
declare global {
  interface Window {
    Pushy?: any;
  }
}

// 获取 Pushy 对象的安全方法
const getPushy = () => {
  // 尝试多种方式获取 Pushy 对象
  if (typeof window !== 'undefined') {
    return window.Pushy || (window as any).Pushy || (globalThis as any).Pushy;
  }
  return undefined;
};

// 双重日志记录：同时发送到 Axiom 和前端调试面板
const dualLog = {
  info: (message: string, data?: any) => {
    logger.info(message, data);
    frontendLogger.info(message, data);
  },
  warn: (message: string, data?: any) => {
    logger.warn(message, data);
    frontendLogger.warn(message, data);
  },
  error: (message: string, data?: any) => {
    logger.error(message, data);
    frontendLogger.error(message, data);
  }
};

// Module-level variables for initialization control
let isInitialized = false;
let retryCount = 0;
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const getDeviceType = () => {
  console.log('🔥 [DEBUG] getDeviceType() called');
  const platform = Capacitor.getPlatform();
  console.log('🔥 [DEBUG] Capacitor.getPlatform() returned:', platform);

  dualLog.info('🔥 [Pushy] Platform detection', {
    capacitorPlatform: platform,
    userAgent: navigator.userAgent
  });

  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
};

// 检测当前 APNs 环境
const getAPNsEnvironment = () => {
  // 在 iOS 上，我们可以通过一些方式推断环境
  if (Capacitor.getPlatform() === 'ios') {
    // 检查是否是开发环境的一些指标
    const isDevelopment =
      // 检查是否有开发相关的标识
      window.location.hostname === 'localhost' ||
      // 或者通过其他方式检测
      (window as any).__DEV__ === true;

    return isDevelopment ? 'sandbox' : 'production';
  }

  return 'unknown';
};

// Function to register device token with backend
const registerDeviceToken = async (deviceToken: string, deviceType: string) => {
  dualLog.info('🔥 [Pushy] registerDeviceToken called', { deviceToken, deviceType });

  try {
    // Check if user is authenticated before making the request
    const { supabase } = await import('../lib/supabase');
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    dualLog.info('🔥 [Pushy] Auth check', {
      hasSession: !!sessionData.session,
      hasUser: !!sessionData.session?.user,
      userId: sessionData.session?.user?.id,
      sessionError: sessionError?.message
    });

    if (sessionError || !sessionData.session?.user) {
      dualLog.error('🔥 [Pushy] User not authenticated, skipping registration');
      return;
    }

    dualLog.info('🔥 [Pushy] Calling fetchApi /pushy/register...');
    const response = await fetchApi('/pushy/register', {
      method: 'POST',
      body: { token: deviceToken, deviceType }
    });
    dualLog.info('🔥 [Pushy] fetchApi response', response);
    dualLog.info('Pushy device token registered successfully');
  } catch (error) {
    dualLog.error('🔥 [Pushy] Failed to register pushy device token', {
      error: error instanceof Error ? error.message : 'Unknown error',
      deviceType,
      tokenLength: deviceToken.length,
      errorStack: error instanceof Error ? error.stack : undefined
    });

    // Retry mechanism
    if (retryCount < MAX_RETRIES) {
      retryCount++;
      dualLog.info(`🔥 [Pushy] Retrying pushy registration (${retryCount}/${MAX_RETRIES}) in ${RETRY_DELAY}ms...`);
      setTimeout(() => registerDeviceToken(deviceToken, deviceType), RETRY_DELAY);
    } else {
      dualLog.error('🔥 [Pushy] Max retries reached for pushy registration');
    }
  }
};



// Early initialization for Pushy - called at app startup
export const initializePushyEarly = () => {
  console.log('🔥 [DEBUG] initializePushyEarly() function called');

  // Platform detection - only initialize on mobile platforms
  const platform = getDeviceType();
  const apnsEnv = getAPNsEnvironment();

  console.log('🔥 [DEBUG] APNs Environment detected:', apnsEnv);
  dualLog.info('🔥 [Pushy] Environment detection', {
    platform,
    apnsEnvironment: apnsEnv,
    isDebugBuild: (window as any).__DEV__ || false
  });

  if (platform !== 'ios' && platform !== 'android') {
    dualLog.info('🔥 [Pushy] Early initialization skipped: not a mobile platform', { platform });
    return;
  }

  const Pushy = getPushy();
  if (!Pushy) {
    dualLog.warn('🔥 [Pushy] Plugin not found during early initialization. Will retry later.');
    return;
  }

  dualLog.info('🔥 [Pushy] Early initialization: calling Pushy.listen()');

  try {
    // According to Capacitor docs, Pushy.listen() should be called early
    Pushy.listen();
    dualLog.info('🔥 [Pushy] Pushy.listen() completed successfully in early initialization');
  } catch (error) {
    dualLog.error('🔥 [Pushy] Error in early Pushy.listen()', { error });
  }
};

// Full initialization for Pushy - called after user authentication
export const initializePushy = () => {
  // Platform detection - only initialize on mobile platforms
  const platform = getDeviceType();
  if (platform !== 'ios' && platform !== 'android') {
    dualLog.info('🔥 [Pushy] Full initialization skipped: not a mobile platform', { platform });
    return;
  }

  // Check if already initialized
  if (isInitialized || pushyManager.isInitialized()) {
    dualLog.info('🔥 [Pushy] Already initialized, skipping');
    return;
  }

  const Pushy = getPushy();
  if (!Pushy) {
    dualLog.warn('🔥 [Pushy] Plugin not found. Not a native device or web push not configured.');
    return;
  }

  // Mark as initialized
  isInitialized = true;
  pushyManager.setInitialized(true);
  dualLog.info('🔥 [Pushy] Marked as initialized');

  dualLog.info('🔥 [Pushy] Starting full initialization (Pushy.register)');

  // Small delay to ensure everything is ready
  setTimeout(() => {
    dualLog.info('🔥 [Pushy] Starting device registration');
    initializePushyRegistration();
  }, 1000);
};

// Separate function for device registration
const initializePushyRegistration = async () => {
  dualLog.info('🔥 [Pushy] Starting device registration');

  const Pushy = getPushy();
  if (!Pushy) {
    dualLog.error('🔥 [Pushy] Pushy object is undefined during registration');
    return;
  }

  try {
    // Check if Pushy object has required methods
    dualLog.info('🔥 [Pushy] Checking Pushy object methods', {
      hasListen: typeof Pushy.listen === 'function',
      hasRegister: typeof Pushy.register === 'function',
      pushyKeys: Object.keys(Pushy || {})
    });

    // Try setting App ID explicitly (from your Dashboard: 6860236c7236ceeb790e3135)
    try {
      dualLog.info('🔥 [Pushy] Setting App ID...');
      Pushy.setAppId('6860236c7236ceeb790e3135');
      dualLog.info('🔥 [Pushy] App ID set successfully');
    } catch (error) {
      dualLog.error('🔥 [Pushy] Failed to set App ID', { error });
    }

    // Check if device is already registered
    try {
      dualLog.info('🔥 [Pushy] Checking if device is already registered...');
      Pushy.isRegistered((isRegistered: boolean) => {
        dualLog.info('🔥 [Pushy] Device registration status', { isRegistered });
        if (isRegistered) {
          dualLog.info('🔥 [Pushy] Device is already registered, attempting to get existing token');
        }
      });
    } catch (error) {
      dualLog.error('🔥 [Pushy] Failed to check registration status', { error });
    }

    // Add a timeout to detect if the callback never fires (increased to 30 seconds)
    let callbackFired = false;
    const timeoutId = setTimeout(() => {
      if (!callbackFired) {
        dualLog.error('🔥 [Pushy] Registration callback timeout - callback never fired after 30 seconds');
        dualLog.error('🔥 [Pushy] This usually indicates:');
        dualLog.error('🔥 [Pushy] 1. Network connectivity issues to Pushy servers');
        dualLog.error('🔥 [Pushy] 2. iOS simulator limitations (try real device)');
        dualLog.error('🔥 [Pushy] 3. Regional restrictions or firewall blocking');
        dualLog.error('🔥 [Pushy] 4. APNs configuration issues');
      }
    }, 30000);

    // Test network connectivity before registration
    dualLog.info('🔥 [Pushy] Testing network connectivity...');
    dualLog.info('🔥 [Pushy] Required domains: *.pushy.me:443 and *.pushy.io:443');

    // Test Pushy-specific endpoints that are required for operation
    const testEndpoints = [
      'https://api.pushy.me/',
      'https://pushy.me/',
      'https://api.pushy.io/',
      'https://pushy.io/',
      'https://google.com/' // Control test
    ];

    testEndpoints.forEach((url, index) => {
      fetch(url, { method: 'GET' })
        .then(response => {
          dualLog.info(`🔥 [Pushy] Network test ${index + 1} result`, {
            url,
            status: response.status,
            ok: response.ok,
            type: response.type,
            rsp: JSON.stringify(response)
          });
        })
        .catch(error => {
          dualLog.error(`🔥 [Pushy] Network test ${index + 1} failed`, {
            url,
            error: error.message
          });

          if (url.includes('pushy')) {
            dualLog.error('🔥 [Pushy] CRITICAL: Cannot reach Pushy servers!');
            dualLog.error('🔥 [Pushy] Please check network firewall settings');
            dualLog.error('🔥 [Pushy] Required whitelist: *.pushy.me:443 and *.pushy.io:443');
          }
        });
    });

    // Register the device for push notifications
    dualLog.info('🔥 [Pushy] Calling Pushy.register()...');

    // 添加更详细的诊断信息
    dualLog.info('🔥 [Pushy] Pre-registration diagnostics', {
      pushyType: typeof Pushy,
      pushyRegisterType: typeof Pushy.register,
      platform: getDeviceType(),
      userAgent: navigator.userAgent,
      isOnline: navigator.onLine,
      timestamp: new Date().toISOString()
    });

    // 检查推送权限（如果可能）
    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' as PermissionName }).then(permission => {
        dualLog.info('🔥 [Pushy] Web notification permission', {
          state: permission.state
        });
      }).catch(error => {
        dualLog.info('🔥 [Pushy] Could not check web notification permission', { error });
      });
    }

    // 检查 Capacitor 推送权限
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const permissionStatus = await PushNotifications.checkPermissions();
      dualLog.info('🔥 [Pushy] Capacitor push notification permissions', {
        receive: permissionStatus.receive
      });

      if (permissionStatus.receive !== 'granted') {
        dualLog.warn('🔥 [Pushy] Push notification permission not granted - this may cause registration to fail');
        dualLog.info('🔥 [Pushy] Attempting to request permissions...');

        const requestResult = await PushNotifications.requestPermissions();
        dualLog.info('🔥 [Pushy] Permission request result', {
          receive: requestResult.receive
        });

        if (requestResult.receive !== 'granted') {
          dualLog.error('🔥 [Pushy] Permission request was denied - Pushy registration will likely fail');
        }
      }
    } catch (error) {
      dualLog.error('🔥 [Pushy] Failed to check/request Capacitor permissions', { error });
    }

    dualLog.info('🔥 [Pushy] About to call Pushy.register() - this should connect to Pushy servers');

    Pushy.register((err: any, deviceToken: string) => {
      callbackFired = true;
      clearTimeout(timeoutId);

      dualLog.info('🔥 [Pushy] Pushy.register callback called', {
        hasError: !!err,
        errorType: typeof err,
        tokenReceived: !!deviceToken,
        tokenLength: deviceToken?.length
      });

      if (err) {
        dualLog.error('🔥 [Pushy] Pushy registration error', {
          error: err,
          errorMessage: err?.message || err?.toString() || 'No error message',
          errorCode: err?.code,
          platform: getDeviceType(),
          errorType: typeof err
        });
        return;
      }

      if (!deviceToken) {
        dualLog.error('🔥 [Pushy] No device token received in callback');
        return;
      }

      dualLog.info('🔥 [Pushy] Device Token received successfully', {
        deviceToken,
        tokenLength: deviceToken.length
      });

      // Store token using pushy manager
      try {
        dualLog.info('🔥 [Pushy] Storing token in pushyManager...');
        pushyManager.setDeviceToken(deviceToken);
        dualLog.info('🔥 [Pushy] Token stored successfully');
      } catch (error) {
        dualLog.error('🔥 [Pushy] Failed to store pushy token', { error });
      }

      // Register device token with backend
      dualLog.info('🔥 [Pushy] Calling registerDeviceToken...');
      registerDeviceToken(deviceToken, getDeviceType());
    });

    dualLog.info('🔥 [Pushy] Pushy.register() call completed, waiting for callback...');

    // Set up notification listeners
    dualLog.info('🔥 [Pushy] Setting up notification listeners...');
    Pushy.setNotificationListener((data: any) => {
      dualLog.info('🔥 [Pushy] Received notification', { data });
      // Example: Display an alert
      alert('Received notification: ' + (data.message || JSON.stringify(data)));
    });

    Pushy.setNotificationClickListener((data: any) => {
      dualLog.info('🔥 [Pushy] Notification clicked', { data });
      // Example: Navigate to a specific page
      // window.location.href = data.url || '/';
    });
    dualLog.info('🔥 [Pushy] Notification listeners set up successfully');

  } catch (error) {
    dualLog.error('🔥 [Pushy] Error in initializePushyRegistration', {
      error: error instanceof Error ? error.message : error,
      errorStack: error instanceof Error ? error.stack : undefined
    });
  }
};

// Export function to get current device token
export const getPushyDeviceToken = (): string | null => {
  return pushyManager.getDeviceToken();
};

// Export function to check if pushy is initialized
export const isPushyInitialized = (): boolean => {
  return pushyManager.isInitialized();
};

// Export function to check if Pushy plugin is available
export const isPushyPluginAvailable = (): boolean => {
  const Pushy = getPushy();
  const isAvailable = !!Pushy;
  dualLog.info('🔥 [Pushy] Plugin availability check', {
    isAvailable,
    platform: getDeviceType(),
    pushyType: typeof Pushy,
    pushyMethods: Pushy ? Object.keys(Pushy) : []
  });
  return isAvailable;
};

// Export function to force reset Pushy state
export const resetPushyState = (): void => {
  dualLog.info('🔥 [Pushy] === FORCE RESET PUSHY STATE ===');

  // Reset module-level variables
  isInitialized = false;
  retryCount = 0;

  // Reset manager state
  pushyManager.setInitialized(false);
  pushyManager.setDeviceToken(null);

  // Clear window storage
  if (typeof window !== 'undefined') {
    (window as any).__pushyDeviceToken = null;
  }

  dualLog.info('🔥 [Pushy] State reset complete - ready for re-initialization');
};

// Export function to force re-register
export const forceRegisterPushy = async (): Promise<void> => {
  dualLog.info('🔥 [Pushy] === FORCE RE-REGISTER ===');

  const Pushy = getPushy();
  if (!Pushy) {
    dualLog.error('🔥 [Pushy] Plugin not available for force register');
    return;
  }

  try {
    // Try to unregister first
    if (typeof Pushy.unregister === 'function') {
      dualLog.info('🔥 [Pushy] Attempting to unregister first...');
      Pushy.unregister();
    }

    // Reset state
    resetPushyState();

    // Wait a bit
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Re-initialize
    dualLog.info('🔥 [Pushy] Starting fresh registration...');
    await initializePushyRegistration();

  } catch (error) {
    dualLog.error('🔥 [Pushy] Error in force re-register', { error });
  }
};

// Export function to run comprehensive diagnostics
export const runPushyDiagnostics = (): void => {
  const Pushy = getPushy();
  const platform = getDeviceType();

  dualLog.info('🔥 [Pushy] === COMPREHENSIVE DIAGNOSTICS ===');
  dualLog.info('🔥 [Pushy] Environment Info', {
    platform,
    userAgent: navigator.userAgent,
    isOnline: navigator.onLine,
    timestamp: new Date().toISOString(),
    windowPushy: !!(window as any).Pushy,
    globalPushy: !!(globalThis as any).Pushy
  });

  if (Pushy) {
    dualLog.info('🔥 [Pushy] Plugin Methods', {
      hasListen: typeof Pushy.listen === 'function',
      hasRegister: typeof Pushy.register === 'function',
      hasSetAppId: typeof Pushy.setAppId === 'function',
      hasIsRegistered: typeof Pushy.isRegistered === 'function',
      allMethods: Object.keys(Pushy)
    });

    // Test basic functionality
    try {
      dualLog.info('🔥 [Pushy] Testing Pushy.isRegistered...');
      Pushy.isRegistered((isRegistered: boolean) => {
        dualLog.info('🔥 [Pushy] isRegistered callback result', { isRegistered });
      });
    } catch (error) {
      dualLog.error('🔥 [Pushy] isRegistered test failed', { error });
    }
  } else {
    dualLog.error('🔥 [Pushy] Plugin not available - possible causes:');
    dualLog.error('🔥 [Pushy] 1. Running in web browser (not native)');
    dualLog.error('🔥 [Pushy] 2. Plugin not properly installed');
    dualLog.error('🔥 [Pushy] 3. Capacitor not fully initialized');
  }

  dualLog.info('🔥 [Pushy] Manager State', {
    isInitialized: pushyManager.isInitialized(),
    deviceToken: pushyManager.getDeviceToken(),
    windowToken: (window as any).__pushyDeviceToken
  });

  dualLog.info('🔥 [Pushy] === DIAGNOSTICS COMPLETE ===');
};
